// Data glue for engineer job photos: ties the IndexedDB photo outbox (db.ts), the
// pure flush orchestration (photoSync.ts) and the API client together. The React
// layer calls these; it never touches IndexedDB or fetch directly.
import { api, ApiError } from "../api";
import { photoOutboxStore, getQueuedPhotosForJob } from "./db";
import { flushPhotoOutbox, classifyPhotoSendError, classifySurveyPhotoSendError } from "./photoSync";
import type { PhotoSendFn, PhotoFlushSummary } from "./photoSync";
import type { QueuedPhoto, SurveyPhotoTarget } from "./types";
import { addSurveyPhotoNotice } from "./surveyStore";

const EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
};

function filenameFor(jobId: string, mimetype: string, id: string): string {
  return `Photo_${jobId}_${id}.${EXT[mimetype.toLowerCase()] ?? "jpg"}`;
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `p_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
}

// Queue a photo. Persists the binary to the outbox immediately (survives a reload /
// the app being killed underground), so it is never lost before it uploads. `geo` carries
// the capture timestamp + (optional) GPS coordinates captured at selection time; capturedAt
// defaults to the queue time when not supplied.
export async function queuePhoto(
  jobId: string,
  blob: Blob,
  mimetype: string,
  geo?: { capturedAt?: string; lat?: number; lng?: number },
): Promise<QueuedPhoto> {
  const queuedAt = new Date().toISOString();
  const item: QueuedPhoto = {
    id: newId(),
    jobId,
    blob,
    mimetype,
    queuedAt,
    capturedAt: geo?.capturedAt ?? queuedAt,
    ...(typeof geo?.lat === "number" ? { lat: geo.lat } : {}),
    ...(typeof geo?.lng === "number" ? { lng: geo.lng } : {}),
    attempts: 0,
  };
  await photoOutboxStore.putPhoto(item);
  return item;
}

// Attach a resolved GPS fix to already-queued photos. Called AFTER queuePhoto so a photo is
// persisted to the outbox first (the never-lost-underground invariant) and the coordinates,
// which can take a moment to acquire, arrive a beat later -- before the photo uploads. Each
// item is re-put by its stable id (so this updates, never duplicates). Best-effort: a photo
// already synced (and removed) is simply re-created and re-uploaded idempotently with its geo.
export async function attachPhotoGeo(items: QueuedPhoto[], geo: { lat: number; lng: number }): Promise<void> {
  for (const item of items) {
    await photoOutboxStore.putPhoto({ ...item, lat: geo.lat, lng: geo.lng });
  }
}

export async function queuedPhotosForJob(jobId: string): Promise<QueuedPhoto[]> {
  return getQueuedPhotosForJob(jobId);
}

// Send one queued photo. The item id is the stable photoId -- the server keys the
// Drive filename off it, so a replay (e.g. after a lost response underground) finds
// and reuses the already-filed copy rather than duplicating it. A 2xx clears the
// item; a 404/410 (job gone) drops it; a network error throws (retryable); any other
// status is kept and surfaced.
// The lifts added on site this app has seen the office hold (Session PJ2 review, LOW 7). In memory, so a flush whose copy of a queued
// photo still says newLift (it was read before markSurveyLiftCreated rewrote it) judges a 404 the same way; the stored mark covers a reload.
const liftsKnownCreated = new Set<string>();

const send: PhotoSendFn = async (item) => {
  // Session PJ2: a survey photo goes to its survey's own route, in its slot. Its refusals are told apart (photoSync.ts).
  if (item.target?.kind === "survey") {
    const t = item.target;
    try {
      await api.projectSurveys.uploadPhoto(t.surveyId, t.photoId, item.blob, filenameFor(t.surveyId, item.mimetype, t.photoId), {
        slotKey: t.slotKey,
        capturedAt: item.capturedAt,
        caption: t.caption,
      });
      // A photo landed, so the office holds its lift: the lift's other queued photos are ordinary survey photos from now on.
      if (t.newLift) liftsKnownCreated.add(t.surveyId);
      // Session PJ2F review L1: a caption typed WHILE the upload was in flight is in the outbox item now but was not in the request.
      // Send it on its own, so the words the person was told were saved are not lost when the item leaves the outbox.
      try {
        const now = (await photoOutboxStore.getPhotoOutbox()).find((i) => i.id === item.id);
        const latest = now?.target?.kind === "survey" ? (now.target.caption ?? "") : (t.caption ?? "");
        if (latest !== (t.caption ?? "")) await api.projectSurveys.setPhotoCaption(t.surveyId, t.photoId, latest);
      } catch {
        /* best effort: the person can set it again from the photo */
      }
      return { ok: true };
    } catch (err) {
      if (err instanceof ApiError) {
        if (t.newLift && err.status === 404 && !liftsKnownCreated.has(t.surveyId)) return { ok: false, drop: false, error: "Waiting for this lift to reach the office." };
        // The reply's error code from its parsed body, never err.code: ApiError.code holds the server's MESSAGE when it sent one.
        return classifySurveyPhotoSendError(err.status, t.surveyLabel ?? "a survey", err.body?.error);
      }
      throw err; // network/offline -> retryable
    }
  }
  try {
    await api.jobs.uploadPhoto(item.jobId, item.id, item.blob, filenameFor(item.jobId, item.mimetype, item.id), {
      capturedAt: item.capturedAt,
      lat: item.lat,
      lng: item.lng,
    });
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError) return classifyPhotoSendError(err.status);
    throw err; // network/offline -> retryable
  }
};

// Drain the photo outbox. Guarded so overlapping triggers (reconnect + foreground +
// the service worker's background-sync message) never run concurrently.
let flushing = false;
export async function syncPhotos(): Promise<PhotoFlushSummary | null> {
  if (flushing) return null;
  flushing = true;
  try {
    return await flushPhotoOutbox(photoOutboxStore, send, { onNotice: addSurveyPhotoNotice });
  } finally {
    flushing = false;
  }
}

// ---- Session PJ2: survey photos ----------------------------------------------------------------------------------------
// Queued exactly as a job photo is (the binary persisted at once, so it survives the app being killed underground), with a
// target naming the survey and slot. The item's id IS the photo id the server keys the row on, so a replay is one row.
// jobId is the visit's job, so the visit's queued photos are found by the same index.
export async function queueSurveyPhoto(
  jobId: string,
  target: Omit<SurveyPhotoTarget, "kind">,
  blob: Blob,
  mimetype: string,
  capturedAt?: string,
): Promise<QueuedPhoto> {
  const queuedAt = new Date().toISOString();
  const item: QueuedPhoto = {
    id: target.photoId,
    jobId,
    blob,
    mimetype,
    queuedAt,
    // Only a time the camera gave is sent as the capture time; the queue time stands in on the phone alone.
    ...(capturedAt ? { capturedAt } : {}),
    attempts: 0,
    target: { kind: "survey", ...target },
  };
  await photoOutboxStore.putPhoto(item);
  return item;
}

/**
 * The office holds this lift added on site now (its add-lift request was answered, the visit lists it, or one of its photos landed).
 * Its queued photos stop being "waiting for the lift to reach the office": from here a 404 means the office removed the survey, so
 * the photo is dropped with the notice naming the lift, instead of waiting for ever (Session PJ2 review, LOW 7). Best effort.
 */
export async function markSurveyLiftCreated(surveyId: string): Promise<void> {
  liftsKnownCreated.add(surveyId);
  try {
    const items = await photoOutboxStore.getPhotoOutbox();
    for (const item of items) {
      if (item.target?.kind !== "survey" || item.target.surveyId !== surveyId || !item.target.newLift) continue;
      const target: SurveyPhotoTarget = { ...item.target };
      delete target.newLift;
      await photoOutboxStore.putPhoto({ ...item, target });
    }
  } catch {
    /* best effort: the next sign that the lift exists tries again */
  }
}

/**
 * Session PJ2F: give a survey photo still on this phone its caption (sent with the photo when it goes up). Empty clears it. Returns
 * whether it was written (best effort: a caption must never break the sheet, but the person is told when it did not stick).
 */
export async function setQueuedPhotoCaption(item: QueuedPhoto, caption: string): Promise<boolean> {
  if (item.target?.kind !== "survey") return false;
  const target: SurveyPhotoTarget = { ...item.target };
  const text = caption.trim().slice(0, 300);
  if (text) target.caption = text;
  else delete target.caption;
  try {
    await photoOutboxStore.putPhoto({ ...item, target });
    return true;
  } catch {
    return false;
  }
}

/** Remove one queued photo from this phone (a retake or a removal before it was sent, or an escalated one let go). */
export async function removeQueuedPhoto(id: string): Promise<void> {
  try {
    await photoOutboxStore.deletePhoto(id);
  } catch {
    /* best effort */
  }
}

/** Put an escalated survey photo back in the queue for one more try. */
export async function retryQueuedPhoto(item: QueuedPhoto): Promise<void> {
  const next: QueuedPhoto = { ...item };
  delete next.status;
  delete next.lastError;
  await photoOutboxStore.putPhoto(next);
}
